/**
 * Copy for the first-login introduction, shown once to anyone without
 * `preferences.firstLoginIntroductionCompleted`.
 *
 * IN ITS OWN MODULE SO THE TESTS CAN IMPORT THE VALUES. The first version of
 * these tests read `Dashboard.tsx` off disk and regex-matched the literals out
 * of it. That suite could pass vacuously: the body regex only matched
 * single-quoted strings, so switching to double quotes, reordering the
 * constants, or moving them relative to the interface below yielded zero
 * matches, and every `for (const body of bodies)` assertion then iterated an
 * empty array and passed. Mutating the copy caught nothing structural, which
 * is exactly how a can't-fail test survives a mutation check.
 *
 * WRITTEN FOR A SALESPERSON, not for whoever bought the product. The copy this
 * replaced explained the software ("focused guidance and practice, not another
 * dashboard to study") and spent a 44-word sentence disclaiming what the
 * Knowledge Base guide would not do. A rep reading that on day one learns
 * nothing they can act on. These say what changes on their next call.
 *
 * House style is recorded in DESIGN_SYSTEM.md ("Product copy") and enforced by
 * `__tests__/dashboardIntroCopy.test.ts`: plain short sentences, second
 * person, concrete over abstract, and no em or en dashes in rendered copy.
 */

export interface IntroCard {
  title: string;
  body: string;
}

/**
 * The dialog's accessible name.
 *
 * "Apexia Labs", not "Sales Guru": SalesGuru is the internal name of the
 * codebase that gets forked and should never reach a user. Apexia Labs is the
 * outward brand of this fork, and it is what the mark rendered directly above
 * this says — `Login.tsx` hardcodes the same string as the logo's alt text.
 *
 * Exported so the gating tests select on this constant instead of restating
 * the literal, which is how the old internal name survived a rewrite of the
 * copy around it.
 */
export const INTRO_DIALOG_LABEL = 'Welcome to Apexia Labs';

export const INTRO_TAGLINE = 'AI built to empower sales teams, not replace them.';

export const INTRO_CARDS_SHARED: readonly IntroCard[] = [
  {
    title: 'Know what to say, live',
    body: 'When a customer objects, the answer is already on your screen. No hunting through documents. No guessing. No "let me get back to you on that".',
  },
  {
    title: 'Practise before it counts',
    body: 'Rehearse against an AI buyer that pushes back like your toughest prospect. Get it wrong here, where it costs nothing, instead of on the real call.',
  },
];

/** The last card is the one useful next step, and it differs by role. */
export const INTRO_LAST_CARD_ADMIN: IntroCard = {
  title: 'Start with your Knowledge Base',
  body: 'Add your objections and answers once. Every rep gets them on every call from then on. If a draft is already waiting, you will pick it up where it was left.',
};

export const INTRO_LAST_CARD_REP: IntroCard = {
  title: 'Jump into Training',
  body: 'Pick an exercise and start talking. Five minutes is enough to feel the difference on your next real conversation.',
};

/** Two shared cards plus the role-specific next step. */
export function introCardsFor(isAdmin: boolean): IntroCard[] {
  return [...INTRO_CARDS_SHARED, isAdmin ? INTRO_LAST_CARD_ADMIN : INTRO_LAST_CARD_REP];
}
